// Paint that sticks to bodies: blood now, any coloured stamp later. Each body
// gets tiles from one shared atlas as children of its own display, so the paint
// moves, layers and goes away with the body and draws in one batch. Stamps are
// kept to the body by a destination-in pass of its shape over the whole tile:
// the shape is drawn into a scratch texture first, because destination-in only
// clears pixels it draws.
import * as PIXI from 'pixi.js';
import { game } from '../../Game';
import { Settings } from '../../Settings';
import { artMapping } from '../gore/goreHalves';

const SCALE = 0.5;
const SLOT = 128;
// Each tile also paints a gutter around itself, so filtering at its edges reads
// its own paint instead of the neighbouring slot's.
const PAD = 2;
const INNER = SLOT - PAD * 2;
const TILE = INNER / SCALE;
const ATLAS_SIZE = 1024;
const MAX_PAGES = 4;
const TILES_PER_FRAME = 8;
const STAMPS_PER_FRAME = 200;

let atlas = null;
let clock = 0;
const hosts = new Map();
const pending = new Set();
const pool = [];
const vec = new Box2D.b2Vec2(0, 0);

const installBlend = renderer => {
	const gl = renderer.gl;
	const map = renderer.state?.blendModesMap;
	if (gl && map && !map['dst-in']) map['dst-in'] = [gl.ZERO, gl.SRC_ALPHA];
	return !!map?.['dst-in'];
};

const getAtlas = () => {
	if (atlas && !atlas.pages[0].destroyed) return atlas;
	const renderer = game.app?.renderer;
	if (!renderer || !installBlend(renderer)) return null;
	const eraser = new PIXI.Graphics().rect(0, 0, SLOT, SLOT).fill(0xffffff);
	eraser.blendMode = 'erase';
	const stencil = PIXI.RenderTexture.create({width: SLOT, height: SLOT});
	const stencilSprite = new PIXI.Sprite(stencil);
	stencilSprite.blendMode = 'dst-in';
	atlas = {pages: [], slots: [], eraser, stencil, stencilSprite, maskScene: new PIXI.Container(), scene: new PIXI.Container(), renderer};
	addPage();
	return atlas;
};

const addPage = () => {
	const texture = PIXI.RenderTexture.create({width: ATLAS_SIZE, height: ATLAS_SIZE});
	atlas.pages.push(texture);
	for (let y = 0; y < ATLAS_SIZE; y += SLOT) {
		for (let x = 0; x < ATLAS_SIZE; x += SLOT) {
			atlas.slots.push({
				target: new PIXI.Texture({source: texture.source, frame: new PIXI.Rectangle(x, y, SLOT, SLOT)}),
				texture: new PIXI.Texture({source: texture.source, frame: new PIXI.Rectangle(x + PAD, y + PAD, INNER, INNER)}),
				tile: null,
			});
		}
	}
};

// How far a tile is outside the screen, in screen pixels, as of the last render.
const offScreen = tile => {
	const t = tile.sprite.worldTransform;
	const x = t.a * INNER / 2 + t.c * INNER / 2 + t.tx;
	const y = t.b * INNER / 2 + t.d * INNER / 2 + t.ty;
	const {width, height} = atlas.renderer.screen;
	return Math.max(0, -x, x - width, -y, y - height);
};

// Where paint goes on a body: its texture container, or its own display.
const hostOf = body => {
	const texture = body.myTexture;
	if (texture && !texture.destroyed && texture.renderable !== false && texture.data) {
		const map = artMapping(texture);
		return {host: texture, point: (x, y) => map.point(x, y), mask: 'texture'};
	}
	const sprite = body.mySprite;
	if (!sprite || sprite.destroyed) return null;
	const sx = sprite.scale.x || 1;
	const sy = sprite.scale.y || 1;
	return {host: sprite, point: (x, y) => ({x: x * Settings.PTM / sx, y: y * Settings.PTM / sy}), mask: 'shapes'};
};

// The body's solid shapes in its display's pixels.
const shapesOf = record => {
	const body = record.body;
	const sprite = record.host;
	const sx = (sprite.scale.x || 1) / Settings.PTM;
	const sy = (sprite.scale.y || 1) / Settings.PTM;
	const shapes = [];
	for (let fixture = body.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) {
		if (fixture.IsSensor()) continue;
		const shape = fixture.GetShape();
		if (shape.GetType() === Box2D.b2Shape.e_circle) {
			const circle = Box2D.castObject(shape, Box2D.b2CircleShape);
			const centre = circle.get_m_p();
			const radius = circle.get_m_radius();
			shapes.push({x: centre.get_x() / sx, y: centre.get_y() / sy, rx: radius / Math.abs(sx), ry: radius / Math.abs(sy)});
		} else if (shape.GetType() === Box2D.b2Shape.e_polygon) {
			const polygon = Box2D.castObject(shape, Box2D.b2PolygonShape);
			const points = [];
			for (let i = 0; i < polygon.get_m_count(); i++) {
				const v = polygon.get_m_vertices(i);
				points.push({x: v.get_x() / sx, y: v.get_y() / sy});
			}
			shapes.push({points});
		}
	}
	return shapes;
};

// The body's art, or every sprite of a graphic group.
const partsOf = host => host.originalSprite ? [host.originalSprite] : host.children.filter(child => child instanceof PIXI.Sprite);

// Where paint can show on a host. Tiles start at its corner and stay inside, so
// a small prop takes one tile and paint hanging over an edge takes none.
const boundsOf = record => {
	const bounds = new PIXI.Bounds();
	if (record.mask === 'texture') {
		for (const part of partsOf(record.host)) {
			part.updateLocalTransform();
			const {minX, minY, maxX, maxY} = part.getLocalBounds();
			bounds.addFrame(minX, minY, maxX, maxY, part.localTransform);
		}
	} else {
		for (const shape of record.shapes) {
			if (shape.points) for (const p of shape.points) bounds.addFrame(p.x, p.y, p.x, p.y);
			else bounds.addFrame(shape.x - shape.rx, shape.y - shape.ry, shape.x + shape.rx, shape.y + shape.ry);
		}
	}
	return bounds.isValid ? bounds : null;
};

const tileFor = (record, tx, ty) => {
	const key = `${tx},${ty}`;
	let tile = record.tiles.get(key);
	if (!tile) record.tiles.set(key, tile = {record, x: record.bounds.minX + tx * TILE, y: record.bounds.minY + ty * TILE, slot: null, sprite: null, mask: null, stamps: [], time: 0});
	return tile;
};

// Stamps paint onto a body at a world point (metres). `size` is in screen
// pixels, `stretch` lengthens the stamp along `angle` for a fast drop.
export const paintStamp = (body, worldX, worldY, {texture, color = 0xffffff, alpha = 1, size = 12, angle = 0, stretch = 1}) => {
	if (body.destroyed) return;
	const found = hostOf(body);
	if (!found) return;
	let record = hosts.get(found.host);
	if (!record) {
		hosts.set(found.host, record = {host: found.host, body, mask: found.mask, tiles: new Map(), layer: null});
		if (record.mask === 'shapes') record.shapes = shapesOf(record);
		record.bounds = boundsOf(record);
	}
	if (!record.bounds) return;
	vec.Set(worldX, worldY);
	const local = body.GetLocalPoint(vec);
	const point = found.point(local.x, local.y);
	const reach = size * Math.max(1, stretch) / 2 + PAD / SCALE;
	const stamp = {x: point.x, y: point.y, texture, color, alpha, size, angle: angle - body.GetAngle(), stretch};
	const {minX, minY, maxX, maxY} = record.bounds;
	const lastX = Math.min(Math.ceil((maxX - minX) / TILE) - 1, Math.floor((point.x + reach - minX) / TILE));
	const lastY = Math.min(Math.ceil((maxY - minY) / TILE) - 1, Math.floor((point.y + reach - minY) / TILE));
	for (let tx = Math.max(0, Math.floor((point.x - reach - minX) / TILE)); tx <= lastX; tx++) {
		for (let ty = Math.max(0, Math.floor((point.y - reach - minY) / TILE)); ty <= lastY; ty++) {
			const tile = tileFor(record, tx, ty);
			tile.stamps.push(stamp);
			pending.add(tile);
		}
	}
};

const freeSlot = () => {
	const isFree = slot => !slot.tile || slot.tile.sprite?.destroyed || slot.tile.record.host.destroyed;
	let free = atlas.slots.find(isFree);
	if (!free && atlas.pages.length < MAX_PAGES) {
		addPage();
		free = atlas.slots.find(isFree);
	}
	// Full: give up the paint furthest off screen, else the paint that is oldest.
	if (!free) {
		let best = -1;
		for (const slot of atlas.slots) {
			const score = offScreen(slot.tile) * 1e6 + clock - slot.tile.time;
			if (score > best) {
				best = score;
				free = slot;
			}
		}
	}
	const old = free.tile;
	if (old) {
		if (old.sprite && !old.sprite.destroyed) old.sprite.destroy();
		old.sprite = null;
		old.slot = null;
	}
	free.tile = null;
	return free;
};

const shapeMask = (record, tile) => {
	const toSlot = (x, y) => [(x - tile.x) * SCALE + PAD, (y - tile.y) * SCALE + PAD];
	const mask = new PIXI.Graphics();
	for (const shape of record.shapes) {
		if (shape.points) {
			mask.poly(shape.points.flatMap(p => toSlot(p.x, p.y))).fill(0xffffff);
		} else {
			const [x, y] = toSlot(shape.x, shape.y);
			mask.ellipse(x, y, shape.rx * SCALE, shape.ry * SCALE).fill(0xffffff);
		}
	}
	return mask;
};

const textureMask = (record, tile) => {
	const mask = new PIXI.Container();
	mask.position.set(PAD - tile.x * SCALE, PAD - tile.y * SCALE);
	mask.scale.set(SCALE);
	for (const part of partsOf(record.host)) {
		const copy = new PIXI.Sprite(part.texture);
		copy.anchor.copyFrom(part.anchor);
		copy.pivot.copyFrom(part.pivot);
		copy.position.copyFrom(part.position);
		copy.scale.copyFrom(part.scale);
		copy.rotation = part.rotation;
		mask.addChild(copy);
	}
	return mask;
};

const stampSprite = (stamp, tile) => {
	const sprite = pool.pop() || new PIXI.Sprite();
	sprite.texture = stamp.texture;
	sprite.anchor.set(0.5);
	sprite.position.set((stamp.x - tile.x) * SCALE + PAD, (stamp.y - tile.y) * SCALE + PAD);
	const base = stamp.size * SCALE / Math.max(1, stamp.texture.width);
	sprite.scale.set(base * stamp.stretch, base);
	sprite.rotation = stamp.angle;
	sprite.tint = stamp.color;
	sprite.alpha = stamp.alpha;
	return sprite;
};

// Once per frame: draws the stamps made since the last flush, a few tiles at a time.
export const flushPaint = () => {
	if (!pending.size || !getAtlas()) return;
	clock++;
	const {scene, eraser, renderer} = atlas;
	let tiles = 0;
	let stamps = 0;
	for (const tile of pending) {
		if (tiles >= TILES_PER_FRAME || stamps >= STAMPS_PER_FRAME) break;
		pending.delete(tile);
		const record = tile.record;
		if (record.host.destroyed || record.body.destroyed) {
			hosts.delete(record.host);
			continue;
		}
		if (!record.layer || record.layer.destroyed) {
			record.layer = new PIXI.Container();
			record.host.addChild(record.layer);
		}
		if (!tile.slot || tile.sprite?.destroyed) {
			tile.slot = freeSlot();
			tile.slot.tile = tile;
			tile.sprite = new PIXI.Sprite(tile.slot.texture);
			tile.sprite.position.set(tile.x, tile.y);
			tile.sprite.scale.set(1 / SCALE);
			record.layer.addChild(tile.sprite);
			scene.addChild(eraser);
		}
		if (!tile.mask) tile.mask = record.mask === 'texture' ? textureMask(record, tile) : shapeMask(record, tile);
		atlas.maskScene.addChild(tile.mask);
		renderer.render({container: atlas.maskScene, target: atlas.stencil, clear: true});
		atlas.maskScene.removeChildren();
		const sprites = tile.stamps.map(stamp => stampSprite(stamp, tile));
		stamps += sprites.length;
		tile.stamps.length = 0;
		tile.time = clock;
		scene.addChild(...sprites, atlas.stencilSprite);
		renderer.render({container: scene, target: tile.slot.target, clear: false});
		scene.removeChildren();
		for (const sprite of sprites) {
			if (pool.length < 256) pool.push(sprite);
			else sprite.destroy();
		}
		tiles++;
	}
	for (const [host, record] of hosts) if (host.destroyed) hosts.delete(record.host);
};

export const paintTileCount = () => [...hosts.values()].reduce((count, record) => count + record.tiles.size, 0);

export const clearPaint = () => {
	hosts.clear();
	pending.clear();
	if (atlas) for (const slot of atlas.slots) slot.tile = null;
};
